import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NominationService, RequestNominationParams } from '@cargos/sprintpay-services';
import { CustomMasks, CustomPatterns } from '@cargos/sprintpay-utils';
import { Subject, finalize, take, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { BreadcrumbsService } from '../../components';
import { PaymentCartTypeDescription, PaymentCartTypeType } from '../../models/payment-type';
import { ConfigService, CurrentFacilityService, SummaryService } from '../../services';
import { PaymentCartTypeHandlerService } from '../../services/payment-cart-type-handler.service';
import { EmailFormType, NominationFormGroupType } from './models/nomination-form';
import { AWBsSelectType, NominationHandlerService } from './services/nomination.service';
import { BlockFirmValidator } from './utils/block-firmscode.validator';
import { formNominationErrors } from './utils/form-errors';

@Component({
    selector: 'app-nomination',
    templateUrl: './nomination.component.html',
    styleUrls: ['./nomination.component.scss'],
    standalone: false,
})
export class NominationComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    private paymentType: PaymentCartTypeType;
    private destination: string | null = '';
    private sourceId: number | null = null;
    public chargesURL = '';
    public nominationForm: NominationFormGroupType;
    public formErrors = formNominationErrors;
    public isLoading: boolean;
    public alphaNumeric = CustomMasks.alphaNumeric;
    public awbs: AWBsSelectType[] = [];
    public allowMultipleSelections = true;
    public paymentTypeDescription: string;
    @Input() blockfirmsCode: string[] = [];
    constructor(
        private configService: ConfigService,
        private formBuilder: NonNullableFormBuilder,
        private location: Location,
        private breadcrumbsService: BreadcrumbsService,
        private requestNominationService: NominationService,
        private paymentCartTypeHandlerService: PaymentCartTypeHandlerService,
        private summaryService: SummaryService,
        private currentFacilityService: CurrentFacilityService,
        private nominationService: NominationHandlerService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.getConfig();
        this.buildForm();
        this.getPaymentCartType();
        this.getDestination();
        this.getSourceId();
        this.updateBreadcrumbs();
        this.subscribeAWBList();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    buildForm(): void {
        this.nominationForm = this.formBuilder.group({
            firmsCode: [
                '',
                [Validators.required, Validators.minLength(4), Validators.maxLength(4)],
                [BlockFirmValidator.checkBlockedFirmsCode(this.blockfirmsCode)],
            ],
            awb: [[] as string[], [Validators.required]],
            companyName: ['', [Validators.required]],
            email: [[] as EmailFormType[], [Validators.required, this.multipleEmails()]],
        });
    }

    multipleEmails(): ValidatorFn {
        return (control: AbstractControl<{ label: string }[]>): ValidationErrors | null => {
            if (control.value == null) {
                return null;
            }

            const results = control.value
                .map((email) => {
                    return !!email.label.match(CustomPatterns.EMAIL);
                })
                .every((result) => result === true);

            return results
                ? null
                : {
                      multipleEmails: true,
                  };
        };
    }

    get firmsCode(): AbstractControl<string> | null {
        return this.nominationForm.get('firmsCode');
    }

    get awb(): AbstractControl<string[]> | null {
        return this.nominationForm.get('awb');
    }

    get companyName(): AbstractControl<string> | null {
        return this.nominationForm.get('companyName');
    }

    get email(): AbstractControl<EmailFormType[]> | null {
        return this.nominationForm.get('email');
    }

    requestNomination(): void {
        if (this.nominationForm.invalid || this.isLoading) {
            return;
        }

        this.isLoading = true;

        const flatAWBValue = this.awb?.value.toString();
        const flatEmailValue = this.email?.value.map((value) => value?.label).toString();

        if (this.paymentType === PaymentCartTypeType.HAWB) {
            this.sendRequest({
                firmsCode: this.firmsCode?.value || '',
                hawb: flatAWBValue,
                companyName: this.companyName?.value || '',
                email: flatEmailValue || '',
                destination: this.destination || '',
                sourceId: this.sourceId || 0,
            });
        } else {
            this.sendRequest({
                firmsCode: this.firmsCode?.value || '',
                awb: flatAWBValue,
                companyName: this.companyName?.value || '',
                email: flatEmailValue || '',
                destination: this.destination || '',
                sourceId: this.sourceId || 0,
            });
        }
    }

    sendRequest(params: RequestNominationParams): void {
        this.requestNominationService
            .requetNomination(params)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: () => {
                    Swal.fire({
                        text: 'Your Nomination Request has been successfully submitted and will be processed momentarily. You will receive an email upon completion.',
                        icon: 'success',
                        customClass: {
                            confirmButton: 'w-100',
                            actions: 'w-100 px-3',
                        },
                        showConfirmButton: true,
                        allowOutsideClick: false,
                    }).then(() => {
                        this.back();
                    });
                },
                error: (error) => {
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: 'warning',
                    });
                },
            });
    }

    back(): void {
        this.location.back();
    }

    onPressCancel(): void {
        this.back();
    }

    updateBreadcrumbs(): void {
        this.breadcrumbsService.sendMessage([
            { section: 'HOME', link: '/', class: '' },
            {
                section: 'CHARGES',
                link: `${this.chargesURL}`,
                class: '',
            },
            {
                section: 'REQUEST NOMINATION',
                link: '/nomination',
                class: 'active',
            },
        ]);
    }

    getConfig(): void {
        this.chargesURL = this.configService.getChargesURL();
    }

    getPaymentCartType(): void {
        this.paymentType = this.paymentCartTypeHandlerService.instant_payment_cart_type;
        this.paymentTypeDescription = PaymentCartTypeDescription[this.paymentType];
    }

    getDestination(): void {
        this.destination = this.summaryService.getLocation();
    }

    getSourceId(): void {
        this.sourceId = this.currentFacilityService.getSourceId();
    }

    subscribeAWBList(): void {
        this.nominationService
            .getAWBList()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((awbs) => {
                if (awbs.length === 1) {
                    this.nominationForm.patchValue({
                        awb: [awbs[0].name],
                    });
                }

                this.nominationForm.patchValue({
                    awb: awbs.map((value) => value?.name),
                });

                this.awbs = awbs;
            });
    }
}
